import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Posts a cart mutation, asks for the owning section back and morphs it.
 *
 * The dispatched event uses `action: 'update'` on purpose: `'add'` makes
 * `<cart-drawer-component auto-open>` pop the drawer, which is wrong for changes
 * the shopper made from inside the cart itself.
 *
 * @param {HTMLElement} host - The component performing the write.
 * @param {string} url - Cart route to post to.
 * @param {Record<string, unknown>} body - JSON body, minus `sections`.
 * @param {{ id: string, quantity: number }[]} lines - Lines for the event payload.
 * @returns {Promise<boolean>} Whether the write succeeded.
 */
async function writeCart(host, url, body, lines) {
  const sectionId = host.dataset.sectionId || '';
  const deferred = CartLinesUpdateEvent.createPromise();

  host.setAttribute('loading', '');

  host.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: lines.map(({ id, quantity }) => ({ merchandiseId: id, quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const payload = JSON.stringify(
      sectionId ? { ...body, sections: [sectionId], sections_url: window.location.pathname } : body
    );

    const response = await fetch(url, fetchConfig('json', { body: payload }));
    const data = await response.json();

    if (!response.ok) throw new Error(data?.description || data?.message || 'Cart update failed');

    deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data) });

    const html = sectionId ? data.sections?.[sectionId] : null;
    if (html) {
      morphSection(sectionId, html, { mode: host.closest('theme-drawer') ? 'hydration' : 'full' });
    }

    return true;
  } catch (error) {
    deferred.reject(error);

    host.dispatchEvent(
      new CartErrorEvent({
        error: error instanceof Error ? error.message : 'Cart update failed',
        code: 'INVALID',
      })
    );

    return false;
  } finally {
    host.removeAttribute('loading');
  }
}

/**
 * One small add-on offer in the cart (snippets/st-cart-upsells.liquid, 21/09/2026).
 *
 * - `/add` adds one unit of the offered variant.
 * - The Wood Diffuser cart offer ($64.95) is only for carts with oil. The section is morphed in
 *   place after every cart change, so the data attributes update and #reconcile runs: when the
 *   offer is in the cart but no oil is left, the offer line is swapped for the regular Wood
 *   Diffuser at full price in one /cart/update.js call (keys: line key -> 0, variant id -> qty).
 *
 * @extends {Component}
 */
class CartUpsells extends Component {
  #busy = false;
  #reconcileQueued = false;

  static observedAttributes = ['data-offer-key', 'data-offer-qty', 'data-has-oil'];

  connectedCallback() {
    super.connectedCallback();
    this.#scheduleReconcile();
  }

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback?.(name, oldValue, newValue);
    if (oldValue !== newValue) this.#scheduleReconcile();
  }

  /** Waits for every morphed attribute to land; rAF never fires in a hidden tab, so a timer races it. */
  #scheduleReconcile() {
    if (this.#reconcileQueued) return;
    this.#reconcileQueued = true;
    const run = () => {
      if (!this.#reconcileQueued) return;
      this.#reconcileQueued = false;
      if (this.isConnected) this.#reconcile();
    };
    requestAnimationFrame(run);
    setTimeout(run, 150);
  }

  async #reconcile() {
    if (this.#busy) return;
    const key = this.dataset.offerKey || '';
    const qty = Number(this.dataset.offerQty) || 0;
    const swap = this.dataset.swapVariant || '';
    if (!key || this.dataset.hasOil === '1' || !swap || qty < 1) return;

    this.#busy = true;
    try {
      await writeCart(this, Theme.routes.cart_update_url, { updates: { [key]: 0, [swap]: qty } }, [
        { id: swap, quantity: qty },
      ]);
    } finally {
      this.#busy = false;
    }
  }

  /**
   * Adds the offered product. Bound from Liquid via `on:click="/add"`.
   * @param {Event} event
   */
  add = async (event) => {
    if (this.#busy) return;
    const button = event.target instanceof Element ? event.target.closest('[data-variant-id]') : null;
    const variantId = button instanceof HTMLElement ? button.dataset.variantId : null;
    if (!variantId) return;
    this.#busy = true;
    try {
      // /cart/add.js answers with the line, not the cart, and the count listeners need a
      // cart. The offer is only shown when it is not in the cart yet, so setting the
      // quantity through /cart/update.js is equivalent to adding one.
      await writeCart(this, Theme.routes.cart_update_url, { updates: { [variantId]: 1 } }, [
        { id: variantId, quantity: 1 },
      ]);
    } finally {
      this.#busy = false;
    }
  };
}

if (!customElements.get('st-cart-upsells')) customElements.define('st-cart-upsells', CartUpsells);


/**
 * Scentreat: "Add to cart" button under a product card (next to "View details"). Announces the
 * add with a real CartLinesUpdateEvent so <cart-drawer-component auto-open> opens the drawer and
 * <cart-items-component> morphs the new line in - same contract as the theme's product form.
 *
 * Markup (custom-liquid block inside the product card):
 *   <st-card-add data-variant-id="123" data-product-url="/products/x"><button type="button">Add to cart</button></st-card-add>
 */
class CardAdd extends HTMLElement {
  #busy = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!button || this.#busy) return;
    event.preventDefault();
    const variantId = this.dataset.variantId;
    if (!variantId) return;

    this.#busy = true;
    button.setAttribute('aria-busy', 'true');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((c) => (c instanceof HTMLElement ? c.dataset.sectionId : ''))
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const body = JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: sectionIds.join(',') });
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const added = await response.json();
      if (!response.ok) throw new Error(added?.description || added?.message || 'Could not add to cart');

      const cart = await (await fetch(Theme.routes.cart_url + '.js', { headers: { Accept: 'application/json' } })).json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'st-card-add',
          sourceId: this.id || variantId,
          itemCount: 1,
          sections: added.sections,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({ error: error instanceof Error ? error.message : 'Could not add to cart', code: 'INVALID' })
      );
      // last resort: the product page still lets the shopper buy
      if (this.dataset.productUrl && !(error instanceof Error && error.name === 'AbortError')) {
        window.location.href = this.dataset.productUrl;
      }
    } finally {
      this.#busy = false;
      button.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('st-card-add')) customElements.define('st-card-add', CardAdd);
